export const ATTENDANCE_POSITION_TIMEOUT_MS = 15000;
export const ATTENDANCE_POSITION_COLLECTION_MS = 2000;
export const ATTENDANCE_POSITION_TARGET_ACCURACY_METERS = 20;

const isValidPosition = (position, startedAt, now) => {
    const { latitude, longitude, accuracy } = position?.coords || {};
    const timestamp = position?.timestamp;

    return Number.isFinite(latitude) && latitude >= -90 && latitude <= 90 &&
        Number.isFinite(longitude) && longitude >= -180 && longitude <= 180 &&
        Number.isFinite(accuracy) && accuracy >= 0 &&
        Number.isFinite(timestamp) && timestamp >= startedAt && timestamp <= now &&
        now - timestamp <= ATTENDANCE_POSITION_TIMEOUT_MS;
};

export const requestAttendancePosition = ({
    onSuccess,
    onError,
    geolocation = window.navigator.geolocation
}) => {
    const startedAt = Date.now();
    const deadline = startedAt + ATTENDANCE_POSITION_TIMEOUT_MS;
    let positions = [];
    let finished = false;
    let unavailable = false;
    let watchId;
    let collectionTimer;
    let deadlineTimer;

    const clearWatch = () => {
        if (watchId === undefined || watchId === null) {
            return;
        }

        const currentWatchId = watchId;
        watchId = undefined;

        try {
            geolocation.clearWatch(currentWatchId);
        } catch (error) {
            // A browser cleanup failure must not reopen a completed attempt.
        }
    };

    const cancel = () => {
        finished = true;
        clearTimeout(collectionTimer);
        clearTimeout(deadlineTimer);
        clearWatch();
        positions = [];
    };

    const fail = (error) => {
        if (finished) {
            return;
        }

        cancel();
        onError(error);
    };

    const getBestPosition = (now) => {
        positions = positions.filter((position) => isValidPosition(position, startedAt, now));

        return positions.reduce((best, position) => {
            if (!best || position.coords.accuracy < best.coords.accuracy || (
                position.coords.accuracy === best.coords.accuracy && position.timestamp > best.timestamp
            )) {
                return position;
            }

            return best;
        }, null);
    };

    const finishIfReady = (forceDeadline = false) => {
        if (finished) {
            return;
        }

        const now = Date.now();
        const bestPosition = getBestPosition(now);
        const deadlineReached = forceDeadline || now >= deadline;
        const accurateEnough = now - startedAt >= ATTENDANCE_POSITION_COLLECTION_MS &&
            bestPosition?.coords.accuracy <= ATTENDANCE_POSITION_TARGET_ACCURACY_METERS;

        if (bestPosition && (deadlineReached || accurateEnough)) {
            cancel();
            onSuccess(bestPosition);
        } else if (deadlineReached) {
            fail({ code: unavailable ? 2 : 3 });
        }
    };

    if (typeof geolocation?.watchPosition !== "function") {
        fail({ code: 2 });
        return cancel;
    }

    // This deadline also covers the time spent waiting for browser permission.
    deadlineTimer = setTimeout(() => finishIfReady(true), ATTENDANCE_POSITION_TIMEOUT_MS);
    collectionTimer = setTimeout(finishIfReady, ATTENDANCE_POSITION_COLLECTION_MS);

    try {
        watchId = geolocation.watchPosition(
            (position) => {
                if (finished) {
                    return;
                }

                const now = Date.now();

                if (now >= deadline) {
                    finishIfReady();
                    return;
                }

                if (isValidPosition(position, startedAt, now)) {
                    positions.push(position);
                } else {
                    unavailable = true;
                }

                finishIfReady();
            },
            (error) => {
                if (finished) {
                    return;
                }

                if (error?.code === 1) {
                    fail(error);
                } else if (Date.now() >= deadline) {
                    finishIfReady();
                } else {
                    unavailable = unavailable || error?.code !== 3;
                }
            },
            {
                enableHighAccuracy: true,
                timeout: ATTENDANCE_POSITION_TIMEOUT_MS,
                maximumAge: 0
            }
        );

        // Some implementations can call back before returning the watch ID.
        if (finished) {
            clearWatch();
        }
    } catch (error) {
        fail({ code: error?.code === 1 || error?.name === "SecurityError" ? 1 : 2 });
    }

    return cancel;
};
